import { Box, Button, CircularProgress, Dialog, IconButton, TextField, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Close from "@mui/icons-material/Close";
import React, { useEffect, useState } from "react";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { sendThumbsDownFeedback } from "src/api/sendThumbsDownFeedback";

export const THUMBS_DOWN_ISSUE_KINDS = ["Response Issue", "Display Issue", "Other"] as const;

type IssueKind = (typeof THUMBS_DOWN_ISSUE_KINDS)[number];
type FormStep = "loading" | "issue" | "describe" | "thanks";

interface ThumbsDownFormProps {
  open: boolean;
  accessAuthToken: string | null;
  messageId?: string;
  message?: string;
  truncatedResponse: string;
  leagueContext?: string;
  shareUrl?: string;
  client?: string;
  currentVersion?: string;
  deviceModel?: string;
  calculatorSettings?: string;
  onClose: () => void;
  onSubmitted: () => void;
}

const POPUP_AUTO_CLOSE_DELAY_MS = 2000; //2 seconds
const OPEN_LOADER_MS = 400;
const orange = PureLightThemeCaaS.colors.alpha.orange[100];

const ThumbsDownForm: React.FC<ThumbsDownFormProps> = ({
  open,
  accessAuthToken,
  messageId,
  message,
  truncatedResponse,
  leagueContext,
  shareUrl,
  client,
  currentVersion,
  deviceModel,
  calculatorSettings,
  onClose,
  onSubmitted,
}) => {
  const [step, setStep] = useState<FormStep>("loading");
  const [issueKind, setIssueKind] = useState<IssueKind | null>(null);
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep("loading");
    setIssueKind(null);
    setDescription("");
    setIsSubmitting(false);
    const timer = setTimeout(() => setStep("issue"), OPEN_LOADER_MS);
    return () => clearTimeout(timer);
  }, [open]);

  const handleSubmit = async () => {
    if (!issueKind || !description.trim() || !accessAuthToken || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await sendThumbsDownFeedback(accessAuthToken, {
        issueKind,
        description: description.trim(),
        message,
        response: truncatedResponse,
        messageId,
        leagueContext,
        shareUrl,
        client,
        currentVersion,
        deviceModel,
        calculatorSettings,
      });
      setStep("thanks");
      setTimeout(() => {
        onSubmitted();
      }, POPUP_AUTO_CLOSE_DELAY_MS);
    } catch {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{
        zIndex: 9999, // Above Quan (zIndex 2000) and other app chrome
        "& .MuiDialog-container": {
          zIndex: 9999,
        },
      }}
      PaperProps={{
        sx: {
          bgcolor: "#000000",
          color: "#ffffff",
          minHeight: { xs: "auto", sm: "auto" },
          borderRadius: 2,
          zIndex: 9999,
          boxShadow: "none",
          border: "1px solid #222222",
          display: "flex",
          flexDirection: "column",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            position: "fixed",
            backdropFilter: "blur(1px) !important",
            WebkitBackdropFilter: "blur(1px) !important",
            backgroundColor: "rgba(0, 0, 0, 0.35) !important",
          },
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1, pt: 1, flexShrink: 0 }}>
        <IconButton
          aria-label="back"
          onClick={step === "describe" ? () => setStep("issue") : onClose}
          sx={{ color: "#ffffff" }}
        >
          <ArrowBackIcon />
        </IconButton>
        <IconButton aria-label="close" onClick={onClose} sx={{ color: "#ffffff" }}>
          <Close />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: step === "thanks" || step === "loading" ? "center" : "center",
          px: 3,
          pb: 4,
          minHeight: 300,
          gap: 2,
        }}
      >
        {step === "loading" && <CircularProgress size={40} sx={{ color: orange }} />}

        {step === "issue" && (
          <>
            <Typography variant="h4" sx={{ textAlign: "center", fontWeight: 600 }}>
              What Kind Of Issue Is This?
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%", maxWidth: 360 }}>
              {THUMBS_DOWN_ISSUE_KINDS.map((kind) => (
                <Button
                  key={kind}
                  variant="outlined"
                  onClick={() => {
                    setIssueKind(kind);
                    setStep("describe");
                  }}
                  sx={{
                    color: "#ffffff",
                    borderColor: "rgba(255,255,255,0.35)",
                    borderRadius: "999px",
                    py: 1.25,
                    textTransform: "none",
                    "&:hover": { borderColor: orange, color: orange },
                  }}
                >
                  {kind}
                </Button>
              ))}
            </Box>
          </>
        )}

        {step === "describe" && (
          <>
            <Typography variant="h5" sx={{ mb: 3, textAlign: "center", fontWeight: 600 }}>
              Describe The Issue
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              autoFocus
              placeholder="Type your answer here..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void handleSubmit();
                }
              }}
              variant="standard"
              InputProps={{
                disableUnderline: false,
                sx: { color: "#ffffff", fontStyle: description ? "normal" : "italic" },
              }}
              sx={{
                maxWidth: 420,
                "& .MuiInput-underline:before": { borderBottomColor: "rgba(255,255,255,0.6)" },
                "& .MuiInput-underline:after": { borderBottomColor: orange },
                "& .MuiInputBase-input::placeholder": { color: "rgba(255,255,255,0.5)", opacity: 1 },
              }}
            />
            <Typography variant="caption" sx={{ mt: 1, color: "rgba(255,255,255,0.7)" }}>
              Shift + Enter to make a line break
            </Typography>
            <Button
              onClick={() => void handleSubmit()}
              disabled={!description.trim() || isSubmitting}
              sx={{ mt: 3, color: orange, textTransform: "none", fontWeight: 600 }}
            >
              {isSubmitting ? <CircularProgress size={22} sx={{ color: orange }} /> : "Submit"}
            </Button>
          </>
        )}

        {step === "thanks" && (
          <Typography variant="h3" sx={{ textAlign: "center", fontWeight: 600, color: "#ffffff" }}>
            Thank you for submitting!
          </Typography>
        )}
      </Box>
    </Dialog>
  );
};

export default ThumbsDownForm;
